import React, { useMemo } from "react";
import { FaRedoAlt, FaTrophy } from "react-icons/fa";

import { getScoreMeta } from "../utils/techniquesQuizzUtils";

export default function QuizzResultsPanel({
  questions = [],
  errors = [],
  score = 0,
  onRestart,
}) {
  const errorMap = useMemo(() => {
    return new Map(errors.map((error) => [error.q.id, error.selected]));
  }, [errors]);

  const scoreMeta = getScoreMeta(score, questions.length);

  return (
    <div>
      <div className="overflow-hidden rounded-[26px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="p-5 sm:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-4">
              <div className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-amber-50 text-amber-600 ring-1 ring-amber-200 dark:bg-amber-900/20 dark:text-amber-200 dark:ring-amber-800/60">
                <FaTrophy className="text-2xl" />
              </div>

              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                  Résultat final
                </p>

                <h2 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
                  {score} / {questions.length}
                </h2>

                <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                  {scoreMeta.message}
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <span
                className={`inline-flex items-center justify-center rounded-2xl px-4 py-3 text-sm font-bold ring-1 ${scoreMeta.className}`}
              >
                {scoreMeta.label}
              </span>

              <button
                type="button"
                onClick={onRestart}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-800"
              >
                <FaRedoAlt />
                Recommencer
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {questions.map((question, index) => {
          const userAnswer = errorMap.get(question.id);
          const isCorrect = !userAnswer;

          return (
            <article
              key={`${question.id}-${index}`}
              className={`overflow-hidden rounded-2xl border shadow-sm ${
                isCorrect
                  ? "border-emerald-200 bg-emerald-50/70 dark:border-emerald-800/60 dark:bg-emerald-900/10"
                  : "border-rose-200 bg-rose-50/70 dark:border-rose-800/60 dark:bg-rose-900/10"
              }`}
            >
              <div className="bg-white dark:bg-slate-950">
                <div className="w-full" style={{ aspectRatio: "551 / 617" }}>
                  <img
                    src={question.image}
                    alt={question.correctAnswer}
                    className="h-full w-full object-contain"
                    loading="lazy"
                  />
                </div>
              </div>

              <div className="space-y-1 p-3 text-center text-xs">
                <p
                  className={`font-bold ${
                    isCorrect
                      ? "text-emerald-700 dark:text-emerald-200"
                      : "text-slate-900 dark:text-white"
                  }`}
                >
                  {question.correctAnswer}
                </p>

                {!isCorrect && (
                  <p className="text-rose-700 line-through dark:text-rose-300">
                    {userAnswer}
                  </p>
                )}

                {question.famille && (
                  <p className="line-clamp-1 text-[11px] text-slate-500 dark:text-slate-400">
                    {question.famille}
                  </p>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}